import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import {
  getDocsIndex, getDoc,
  type DocsCategory, type DocEntry,
} from '../api'
import {
  BookOpen, Rocket, Cpu, Globe, Play, Shield, Wrench,
  Search, ChevronRight, FileText, ArrowLeft,
  ChevronUp,
} from 'lucide-react'

const ICON_MAP: Record<string, typeof BookOpen> = {
  rocket: Rocket,
  cpu: Cpu,
  globe: Globe,
  play: Play,
  shield: Shield,
  wrench: Wrench,
}

const WARN_KEYWORDS = ['важно', 'внимание', 'warning', 'осторожно', 'danger', 'caution', 'обязательно']

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function inlineFmt(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong class="doc-b">$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="doc-ic">$1</code>')
}

function renderMarkdown(md: string): string {
  const blocks: string[] = []

  let text = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
    const i = blocks.length
    const langLabel = lang || 'code'
    const codeText = code.trimEnd()
    const encoded = btoa(unescape(encodeURIComponent(codeText)))

    const lines = codeText.split('\n')
    const numberedLines = lines.map((line: string, idx: number) => {
      const num = idx + 1
      const escaped = esc(line)
      let cls = ''
      if (/^\s*#/.test(line)) cls = ' doc-line-comment'
      else if (/^\s*\$/.test(line)) cls = ' doc-line-cmd'
      return `<span class="doc-code-line${cls}"><span class="doc-line-num">${num}</span>${escaped}</span>`
    }).join('\n')

    const copyIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>'

    blocks.push(
      `<div class="doc-cb"><div class="doc-cb-head">` +
      `<span class="doc-cb-dots"><i></i><i></i><i></i></span>` +
      `<div class="doc-cb-head-right">` +
      `<span class="doc-cb-lang">${esc(langLabel)}</span>` +
      `<button class="doc-copy-btn" data-code="${encoded}" title="Копировать">${copyIconSvg}</button>` +
      `</div>` +
      `</div><pre class="doc-pre"><code>${numberedLines}</code></pre></div>`
    )
    return `\x00B${i}\x00`
  })

  text = text.replace(/\|(.+)\|\n\|[-| :]+\|\n((?:\|.+\|\n?)*)/g, (_m, header, rows) => {
    const i = blocks.length
    const ths = header.split('|').filter(Boolean).map((h: string) =>
      `<th>${inlineFmt(h.trim())}</th>`
    ).join('')
    const trs = rows.trim().split('\n').filter(Boolean).map((row: string) => {
      const tds = row.split('|').filter(Boolean).map((c: string, ci: number) =>
        `<td${ci === 0 ? ' class="doc-td-key"' : ''}>${inlineFmt(c.trim())}</td>`
      ).join('')
      return `<tr>${tds}</tr>`
    }).join('')
    blocks.push(`<div class="doc-tw"><table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></div>`)
    return `\x00B${i}\x00`
  })

  const lines = text.split('\n')
  const out: string[] = []
  let inList = false, lt = ''
  const closeList = () => { if (inList) { out.push(lt === 'ul' ? '</ul>' : '</ol>'); inList = false } }

  for (const line of lines) {
    const bm = line.match(/^\x00B(\d+)\x00$/)
    if (bm) { closeList(); out.push(blocks[+bm[1]]); continue }
    if (/^---+$/.test(line)) { closeList(); out.push('<div class="doc-sep"></div>'); continue }

    const h1 = line.match(/^# (.+)$/);  if (h1) { closeList(); out.push(`<h1 class="doc-h1">${inlineFmt(h1[1])}</h1>`); continue }
    const h2 = line.match(/^## (.+)$/);  if (h2) {
      closeList()
      const id = 'h-' + h2[1].replace(/[^\w\u0400-\u04FF]+/g, '-').toLowerCase()
      out.push(`<h2 class="doc-h2" id="${id}">${inlineFmt(h2[1])}</h2>`)
      continue
    }
    const h3 = line.match(/^### (.+)$/); if (h3) { closeList(); out.push(`<h3 class="doc-h3">${inlineFmt(h3[1])}</h3>`); continue }

    const bq = line.match(/^> (.+)$/)
    if (bq) {
      closeList()
      const isWarn = WARN_KEYWORDS.some(w => bq[1].toLowerCase().includes(w))
      const iconCls = isWarn ? 'doc-bq-icon doc-bq-icon-warn' : 'doc-bq-icon doc-bq-icon-info'
      const iconSvg = isWarn
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>'
      out.push(`<blockquote class="doc-bq${isWarn ? ' doc-bq-warn' : ''}"><span class="${iconCls}">${iconSvg}</span><span class="doc-bq-text">${inlineFmt(bq[1])}</span></blockquote>`)
      continue
    }

    const ul = line.match(/^[-*] (.+)$/)
    if (ul) {
      if (!inList || lt !== 'ul') { closeList(); out.push('<ul class="doc-ul">'); inList = true; lt = 'ul' }
      out.push(`<li>${inlineFmt(ul[1])}</li>`); continue
    }
    const ol = line.match(/^\d+\. (.+)$/)
    if (ol) {
      if (!inList || lt !== 'ol') { closeList(); out.push('<ol class="doc-ol">'); inList = true; lt = 'ol' }
      out.push(`<li>${inlineFmt(ol[1])}</li>`); continue
    }

    closeList()
    if (line.trim() === '') continue
    out.push(`<p class="doc-p">${inlineFmt(line)}</p>`)
  }
  closeList()
  return out.join('\n')
}

interface TocItem {
  id: string
  text: string
}

function extractTocFromHtml(html: string): TocItem[] {
  const re = /<h2[^>]*id="([^"]*)"[^>]*>(.*?)<\/h2>/gi
  const items: TocItem[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    const text = m[2].replace(/<[^>]+>/g, '')
    items.push({ id: m[1], text })
  }
  return items
}

export default function AdminDocs() {
  const [categories, setCategories] = useState<DocsCategory[]>([])
  const [activeDoc, setActiveDoc] = useState<string | null>(null)
  const [docContent, setDocContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [scrollProgress, setScrollProgress] = useState(0)
  const [activeTocId, setActiveTocId] = useState('')
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getDocsIndex().then(idx => setCategories(idx.categories || [])).catch(() => {})
  }, [])

  const loadDoc = useCallback(async (docId: string) => {
    setLoading(true)
    setActiveDoc(docId)
    setScrollProgress(0)
    try {
      const data = await getDoc(docId)
      setDocContent(data.content || '')
    } catch { setDocContent('Ошибка загрузки документа') }
    setLoading(false)
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return categories
    const q = search.toLowerCase()
    return categories.map(cat => ({
      ...cat,
      docs: cat.docs.filter((d: DocEntry) => d.title.toLowerCase().includes(q) || d.id.includes(q)),
    })).filter(cat => cat.docs.length > 0 || cat.title.toLowerCase().includes(q))
  }, [categories, search])

  const htmlContent = useMemo(() => renderMarkdown(docContent), [docContent])
  const tocItems = useMemo(() => extractTocFromHtml(htmlContent), [htmlContent])

  const activeTitle = useMemo(() => {
    if (!activeDoc) return ''
    for (const cat of categories)
      for (const d of cat.docs)
        if (d.id === activeDoc) return d.title
    return ''
  }, [activeDoc, categories])

  const activeCategory = useMemo(() => {
    if (!activeDoc) return null
    for (const cat of categories)
      for (const d of cat.docs)
        if (d.id === activeDoc) return cat
    return null
  }, [activeDoc, categories])

  const totalDocs = useMemo(() =>
    categories.reduce((sum, c) => sum + c.docs.length, 0),
    [categories]
  )

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !activeDoc) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const max = scrollHeight - clientHeight
      setScrollProgress(max > 0 ? scrollTop / max : 0)
    }
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [activeDoc])

  useEffect(() => {
    if (!activeDoc || tocItems.length === 0) return
    const container = scrollContainerRef.current
    if (!container) return

    const headings = tocItems.map(t => container.querySelector(`#${CSS.escape(t.id)}`)).filter(Boolean) as HTMLElement[]
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveTocId(entry.target.id)
          }
        }
      },
      { root: container, rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    )
    headings.forEach(h => observer.observe(h))
    return () => observer.disconnect()
  }, [activeDoc, tocItems])

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      const copyBtn = target.closest('.doc-copy-btn') as HTMLElement | null
      if (copyBtn) {
        e.preventDefault()
        e.stopPropagation()
        const encoded = copyBtn.getAttribute('data-code')
        if (encoded) {
          const code = decodeURIComponent(escape(atob(encoded)))
          navigator.clipboard.writeText(code)
          copyBtn.classList.add('doc-copied')
          copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
          setTimeout(() => {
            copyBtn.classList.remove('doc-copied')
            copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>'
          }, 2000)
        }
        return
      }

      if (target.classList.contains('doc-ic')) {
        const text = target.textContent || ''
        navigator.clipboard.writeText(text)
        const tooltip = document.createElement('span')
        tooltip.className = 'doc-ic-tooltip'
        tooltip.textContent = 'Скопировано!'
        target.style.position = 'relative'
        target.appendChild(tooltip)
        target.classList.add('doc-ic-flash')
        setTimeout(() => {
          tooltip.remove()
          target.classList.remove('doc-ic-flash')
        }, 1500)
        return
      }

      if (target.classList.contains('doc-td-key') || target.closest('.doc-td-key')) {
        const cell = (target.classList.contains('doc-td-key') ? target : target.closest('.doc-td-key')) as HTMLElement
        const text = cell.textContent || ''
        navigator.clipboard.writeText(text)
        cell.classList.add('doc-td-flash')
        setTimeout(() => cell.classList.remove('doc-td-flash'), 600)
        return
      }
    }

    el.addEventListener('click', handleClick)
    return () => el.removeEventListener('click', handleClick)
  }, [htmlContent])

  const scrollToTocItem = useCallback((id: string) => {
    const container = scrollContainerRef.current
    if (!container) return
    const el = container.querySelector(`#${CSS.escape(id)}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const startHereDocId = useMemo(() => {
    for (const cat of categories)
      for (const d of cat.docs)
        if (d.title.toLowerCase().includes('передача проекта') || d.id.includes('handover'))
          return d.id
    return null
  }, [categories])

  return (
    <>
      <style>{STYLES}</style>
      <div className="flex h-full" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        <div className="w-64 shrink-0 border-r border-border overflow-y-auto bg-card/50 doc-sidebar">
          <div className="p-4 border-b border-border/60">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-primary" />
              <h2 className="font-semibold text-[13px] tracking-tight">Документация</h2>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск..."
                className="h-8 pl-8 text-xs bg-muted/40 border-border/40"
              />
            </div>
          </div>
          <nav className="p-2">
            {filtered.map(cat => {
              const Icon = ICON_MAP[cat.icon] || FileText
              return (
                <div key={cat.id} className="mb-1" id={`sidebar-cat-${cat.id}`}>
                  <div className="flex items-center gap-2 px-2 py-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest">
                    <Icon className="w-3 h-3" />
                    {cat.title}
                  </div>
                  {cat.docs.map((doc: DocEntry) => (
                    <button
                      key={doc.id}
                      onClick={() => loadDoc(doc.id)}
                      className={`w-full text-left px-2 py-[5px] rounded-md text-[13px] transition-all flex items-center gap-1.5 ${
                        activeDoc === doc.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                      }`}
                    >
                      <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${activeDoc === doc.id ? 'rotate-90 text-primary' : 'text-muted-foreground/30'}`} />
                      {doc.title}
                    </button>
                  ))}
                </div>
              )
            })}
          </nav>
        </div>

        <div className="flex-1 overflow-hidden relative" style={{ display: 'flex', flexDirection: 'column' }}>
          {activeDoc && (
            <div className="doc-progress-bar" style={{ transform: `scaleX(${scrollProgress})` }} />
          )}

          <div className="flex-1 overflow-y-auto doc-scroll-area" ref={scrollContainerRef}>
            {!activeDoc ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-1 doc-landing">
                <BookOpen className="w-12 h-12 mb-2 opacity-15" />
                <p className="text-base font-semibold tracking-tight">Документация</p>
                <p className="text-xs text-muted-foreground/60 mb-6">Выберите раздел в навигации</p>
                <div className="doc-landing-grid">
                  {categories.map(cat => {
                    const Icon = ICON_MAP[cat.icon] || FileText
                    const isStartHere = cat.docs.some(d => d.id === startHereDocId)
                    return (
                      <button
                        key={cat.id}
                        onClick={() => cat.docs[0] && loadDoc(cat.docs[0].id)}
                        className={`doc-landing-card${isStartHere ? ' doc-landing-card-start' : ''}`}
                      >
                        {isStartHere && <span className="doc-start-badge">Start Here</span>}
                        <Icon className="w-6 h-6 doc-landing-icon" />
                        <span className="doc-landing-title">{cat.title}</span>
                        <Badge variant="secondary" className="text-[9px] h-4 px-1.5 doc-landing-badge">{cat.docs.length}</Badge>
                      </button>
                    )
                  })}
                </div>
                {totalDocs > 0 && (
                  <p className="doc-landing-footer">{totalDocs} документов в {categories.length} категориях</p>
                )}
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : (
              <div className="doc-layout">
                <article className="doc-root">
                  <div className="doc-nav">
                    <button
                      onClick={() => { setActiveDoc(null); setDocContent(''); setScrollProgress(0) }}
                      className="doc-nav-back"
                    >
                      <ArrowLeft className="w-3 h-3" /> Документация
                    </button>
                    {activeCategory && (
                      <>
                        <span className="doc-nav-sep">/</span>
                        <button
                          className="doc-nav-cat"
                          onClick={() => {
                            const el = document.getElementById(`sidebar-cat-${activeCategory.id}`)
                            el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }}
                        >
                          {activeCategory.title}
                        </button>
                      </>
                    )}
                    {activeTitle && <span className="doc-nav-sep">/</span>}
                    {activeTitle && <span className="doc-nav-cur">{activeTitle}</span>}
                  </div>
                  <div className="doc-content" ref={contentRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />
                </article>

                {tocItems.length > 1 && (
                  <aside className="doc-toc">
                    <div className="doc-toc-title">Содержание</div>
                    {tocItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => scrollToTocItem(item.id)}
                        className={`doc-toc-item${activeTocId === item.id ? ' doc-toc-active' : ''}`}
                      >
                        {item.text}
                      </button>
                    ))}
                  </aside>
                )}
              </div>
            )}

            {activeDoc && !loading && scrollProgress > 0.3 && (
              <button className="doc-scroll-top" onClick={scrollToTop}>
                <ChevronUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const STYLES = /* css */`
/* ═══ helper: cm(color, pct) = color-mix ═══ */
/* ═══════ Layout ═══════ */
.doc-layout { display: flex; gap: 0; position: relative; }
.doc-root {
  max-width: 48rem; margin: 0 auto; padding: 1.5rem 2.5rem 6rem;
  user-select: text !important; -webkit-user-select: text !important;
  cursor: auto !important; flex: 1; min-width: 0;
}
.doc-content { user-select: text !important; -webkit-user-select: text !important; cursor: auto !important; }
.doc-content * { cursor: auto !important; user-select: text !important; -webkit-user-select: text !important; }
.doc-content .doc-copy-btn, .doc-content button { user-select: none !important; -webkit-user-select: none !important; cursor: pointer !important; }

/* ═══════ Progress Bar ═══════ */
.doc-progress-bar {
  position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: var(--primary); transform-origin: left;
  z-index: 20; transition: transform 0.15s ease-out; pointer-events: none;
}

/* ═══════ Scroll to Top ═══════ */
.doc-scroll-top {
  position: fixed; bottom: 2rem; right: 2rem; width: 36px; height: 36px;
  border-radius: 50%; background: var(--primary); color: var(--primary-foreground);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px color-mix(in oklch, var(--primary) 30%, transparent);
  transition: all 0.2s; z-index: 30; opacity: 0.8;
  animation: doc-fade-in 0.2s ease-out;
}
.doc-scroll-top:hover {
  opacity: 1; transform: translateY(-2px);
  box-shadow: 0 6px 16px color-mix(in oklch, var(--primary) 40%, transparent);
}
@keyframes doc-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 0.8; transform: translateY(0); } }

/* ═══════ Scrollbar ═══════ */
.doc-scroll-area { background: var(--background); }
.doc-scroll-area::-webkit-scrollbar { width: 6px; }
.doc-scroll-area::-webkit-scrollbar-track { background: transparent; }
.doc-scroll-area::-webkit-scrollbar-thumb { background: color-mix(in oklch, var(--muted-foreground) 20%, transparent); border-radius: 3px; }
.doc-scroll-area::-webkit-scrollbar-thumb:hover { background: color-mix(in oklch, var(--muted-foreground) 35%, transparent); }
.doc-sidebar::-webkit-scrollbar { width: 4px; }
.doc-sidebar::-webkit-scrollbar-track { background: transparent; }
.doc-sidebar::-webkit-scrollbar-thumb { background: color-mix(in oklch, var(--muted-foreground) 15%, transparent); border-radius: 2px; }

/* ═══════ Breadcrumb / Nav ═══════ */
.doc-nav { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 2rem; flex-wrap: wrap; }
.doc-nav-back { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.7rem; font-weight: 500; color: var(--muted-foreground); opacity: 0.6; transition: all 0.15s; letter-spacing: 0.01em; }
.doc-nav-back:hover { color: var(--primary); opacity: 1; }
.doc-nav-sep { font-size: 0.65rem; color: var(--muted-foreground); opacity: 0.3; }
.doc-nav-cur { font-size: 0.7rem; font-weight: 500; color: var(--foreground); opacity: 0.6; }
.doc-nav-cat { font-size: 0.7rem; font-weight: 500; color: var(--muted-foreground); opacity: 0.6; transition: all 0.15s; }
.doc-nav-cat:hover { color: var(--primary); opacity: 1; }

/* ═══════ TOC ═══════ */
.doc-toc { position: sticky; top: 1.5rem; align-self: flex-start; width: 180px; shrink: 0; padding: 1rem 0.75rem; margin-right: 1rem; display: none; }
@media (min-width: 1200px) { .doc-toc { display: block; } }
.doc-toc-title { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted-foreground); opacity: 0.5; margin-bottom: 0.65rem; padding-left: 0.5rem; }
.doc-toc-item { display: block; width: 100%; text-align: left; font-size: 0.68rem; line-height: 1.4; padding: 0.3rem 0.5rem; border-left: 2px solid transparent; color: var(--muted-foreground); opacity: 0.5; transition: all 0.15s; border-radius: 0 0.25rem 0.25rem 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.doc-toc-item:hover { color: var(--foreground); opacity: 0.7; background: color-mix(in oklch, var(--muted) 35%, transparent); }
.doc-toc-active { color: var(--primary) !important; border-left-color: var(--primary) !important; background: color-mix(in oklch, var(--primary) 10%, transparent); font-weight: 600; opacity: 1 !important; }

/* ═══════ Headings ═══════ */
.doc-h1 {
  font-size: 1.75rem; font-weight: 800; letter-spacing: -0.035em; line-height: 1.2;
  color: var(--foreground); margin-bottom: 0.25rem; padding-bottom: 1rem;
  border-bottom: 2px solid color-mix(in oklch, var(--primary) 25%, transparent);
}
.doc-h2 {
  font-size: 1.1rem; font-weight: 700; letter-spacing: -0.02em;
  margin-top: 2.75rem; margin-bottom: 0.85rem;
  padding: 0.6rem 0 0.6rem 0.85rem; color: var(--foreground);
  position: relative; scroll-margin-top: 1rem;
  border-top: 1px solid color-mix(in oklch, var(--border) 50%, transparent); padding-top: 2rem;
}
.doc-h2::before {
  content: ''; position: absolute; left: 0; top: 2rem; bottom: 0.1em;
  width: 3px; border-radius: 2px; background: var(--primary); opacity: 0.7;
}
.doc-h3 {
  font-size: 0.9rem; font-weight: 700; letter-spacing: -0.01em;
  margin-top: 2rem; margin-bottom: 0.5rem; color: var(--foreground);
  padding: 0.4rem 0.7rem;
  background: color-mix(in oklch, var(--muted) 50%, transparent);
  border-radius: 0.35rem;
  border-left: 3px solid color-mix(in oklch, var(--primary) 50%, transparent);
  display: inline-block;
}

/* ═══════ Body text ═══════ */
.doc-p { font-size: 0.84rem; line-height: 1.75; color: var(--foreground); opacity: 0.82; margin-bottom: 0.75rem; }
.doc-b { font-weight: 600; opacity: 1; color: var(--foreground); }

/* ═══════ Separator ═══════ */
.doc-sep {
  height: 1px; margin: 2.5rem 0;
  background: linear-gradient(90deg, transparent, var(--border) 20%, var(--border) 80%, transparent);
  opacity: 0.4;
}

/* ═══════ Lists ═══════ */
.doc-ul, .doc-ol { margin: 0.4rem 0 1rem; padding-left: 0; list-style: none; }
.doc-ol { counter-reset: ol-n; }
.doc-ul > li, .doc-ol > li { font-size: 0.84rem; line-height: 1.75; color: var(--foreground); opacity: 0.82; padding-left: 1.4rem; position: relative; margin-bottom: 0.2rem; }
.doc-ul > li::before { content: ''; position: absolute; left: 0.35rem; top: 0.65em; width: 5px; height: 5px; border-radius: 1px; background: var(--primary); opacity: 0.5; transform: rotate(45deg); }
.doc-ol > li { counter-increment: ol-n; }
.doc-ol > li::before { content: counter(ol-n); position: absolute; left: 0; top: 0; width: 1.1rem; text-align: center; font-size: 0.65rem; font-weight: 700; line-height: 1.7; color: var(--primary); opacity: 0.6; font-variant-numeric: tabular-nums; }

/* ═══════ Inline code ═══════ */
.doc-ic {
  font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, monospace;
  font-size: 0.76em; padding: 0.15em 0.45em; border-radius: 0.3rem;
  background: color-mix(in oklch, var(--primary) 15%, transparent);
  color: var(--primary); border: 1px solid color-mix(in oklch, var(--primary) 25%, transparent);
  font-weight: 500; cursor: pointer !important; transition: all 0.15s; position: relative;
}
.doc-ic:hover { background: color-mix(in oklch, var(--primary) 20%, transparent); border-color: color-mix(in oklch, var(--primary) 35%, transparent); }
.doc-ic-flash { background: color-mix(in oklch, var(--primary) 30%, transparent) !important; }
.doc-ic-tooltip {
  position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
  background: var(--foreground); color: var(--background);
  font-size: 0.6rem; font-weight: 600; padding: 0.2rem 0.5rem; border-radius: 0.25rem;
  white-space: nowrap; pointer-events: none; animation: doc-tooltip-in 0.15s ease-out; z-index: 50;
}
.doc-ic-tooltip::after { content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%); border: 4px solid transparent; border-top-color: var(--foreground); }
@keyframes doc-tooltip-in { from { opacity: 0; transform: translateX(-50%) translateY(4px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

/* ═══════ Code block ═══════ */
.doc-cb {
  margin: 1.25rem 0 1.5rem; border-radius: 0.6rem; overflow: hidden;
  border: 1px solid #2a2d35; background: #13161c;
  border-left: 3px solid color-mix(in oklch, var(--primary) 40%, transparent);
  animation: doc-cb-in 0.3s ease-out;
}
@keyframes doc-cb-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.doc-cb-head { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.9rem; background: #1a1d25; border-bottom: 1px solid #2a2d35; }
.doc-cb-head-right { display: flex; align-items: center; gap: 0.6rem; }
.doc-cb-dots { display: flex; gap: 5px; }
.doc-cb-dots i { width: 8px; height: 8px; border-radius: 50%; display: block; }
.doc-cb-dots i:nth-child(1) { background: #ff5f57; }
.doc-cb-dots i:nth-child(2) { background: #febc2e; }
.doc-cb-dots i:nth-child(3) { background: #28c840; }
.doc-cb-lang { font-size: 0.6rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; font-family: ui-monospace, monospace; }

/* Copy button */
.doc-copy-btn { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 0.35rem; color: #6b7280; transition: all 0.15s; opacity: 0.5; cursor: pointer !important; }
.doc-copy-btn:hover { opacity: 1; color: #d1d5db; background: #2a2d35; }
.doc-copied { opacity: 1 !important; color: #28c840 !important; }

.doc-pre { margin: 0; padding: 1rem 1.15rem 1rem 0; overflow-x: auto; font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, monospace; font-size: 0.73rem; line-height: 1.65; color: #c9d1d9; tab-size: 4; white-space: pre; background: transparent; counter-reset: line; }
.doc-pre code { font-family: inherit; font-size: inherit; background: none; border: none; padding: 0; color: inherit; display: block; }

/* Line numbers */
.doc-code-line { display: block; }
.doc-line-num { display: inline-block; width: 3rem; padding-right: 1rem; text-align: right; color: #3d4451; user-select: none; -webkit-user-select: none; font-size: 0.65rem; vertical-align: baseline; }
.doc-line-comment { color: #6b7280; font-style: italic; }
.doc-line-cmd { color: #7ee787; }
.doc-pre::-webkit-scrollbar { height: 5px; }
.doc-pre::-webkit-scrollbar-track { background: transparent; }
.doc-pre::-webkit-scrollbar-thumb { background: #3d4451; border-radius: 3px; }

/* ═══════ Tables ═══════ */
.doc-tw {
  margin: 1.25rem 0 1.75rem; border-radius: 0.6rem;
  border: 1px solid var(--border); overflow-x: auto; position: relative;
  background: var(--card);
}
.doc-tw table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.doc-tw thead tr { background: color-mix(in oklch, var(--primary) 15%, transparent); }
.doc-tw th {
  padding: 0.65rem 1rem; text-align: left; font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.04em; color: var(--primary);
  border-bottom: 2px solid color-mix(in oklch, var(--primary) 35%, transparent);
  border-right: 1px solid color-mix(in oklch, var(--border) 40%, transparent);
}
.doc-tw th:last-child { border-right: none; }
.doc-tw td {
  padding: 0.6rem 1rem;
  border-bottom: 1px solid color-mix(in oklch, var(--border) 50%, transparent);
  border-right: 1px solid color-mix(in oklch, var(--border) 25%, transparent);
  color: var(--foreground); opacity: 0.85; line-height: 1.6; vertical-align: top; transition: background 0.15s;
}
.doc-tw td:last-child { border-right: none; }
.doc-td-key {
  color: var(--foreground) !important; opacity: 1 !important; font-weight: 600; white-space: nowrap;
  font-family: ui-monospace, 'Cascadia Code', Menlo, monospace; font-size: 0.78rem;
  background: color-mix(in oklch, var(--muted) 50%, transparent);
  cursor: pointer !important; position: relative; transition: all 0.15s;
}
.doc-td-key:hover { background: color-mix(in oklch, var(--primary) 15%, transparent) !important; }
.doc-td-flash { animation: doc-cell-flash 0.6s ease-out; }
@keyframes doc-cell-flash { 0% { background: color-mix(in oklch, var(--primary) 30%, transparent); } 100% { background: color-mix(in oklch, var(--muted) 35%, transparent); } }
.doc-tw tbody tr:last-child td { border-bottom: none; }
.doc-tw tbody tr:nth-child(even) { background: color-mix(in oklch, var(--muted) 30%, transparent); }
.doc-tw tbody tr:hover { background: color-mix(in oklch, var(--primary) 12%, transparent); }
.doc-tw td .doc-ic, .doc-tw th .doc-ic { font-size: 0.75em; padding: 0.1em 0.35em; }
.doc-tw::-webkit-scrollbar { height: 5px; }
.doc-tw::-webkit-scrollbar-track { background: color-mix(in oklch, var(--muted) 20%, transparent); border-radius: 3px; }
.doc-tw::-webkit-scrollbar-thumb { background: color-mix(in oklch, var(--muted-foreground) 20%, transparent); border-radius: 3px; }

/* ═══════ Blockquote ═══════ */
.doc-bq {
  margin: 1.25rem 0; padding: 0.85rem 1.1rem;
  border-left: 3px solid var(--primary);
  background: color-mix(in oklch, var(--primary) 10%, transparent);
  border-radius: 0 0.5rem 0.5rem 0; font-size: 0.84rem; line-height: 1.65;
  color: var(--foreground); opacity: 0.9;
  border: 1px solid color-mix(in oklch, var(--primary) 20%, transparent);
  border-left: 3px solid var(--primary);
  display: flex; align-items: flex-start; gap: 0.6rem;
}
.doc-bq-warn { border-left-color: #eab308 !important; background: color-mix(in oklch, #eab308 8%, transparent); border-color: color-mix(in oklch, #eab308 20%, transparent); border-left-color: #eab308 !important; }
.doc-bq-icon { flex-shrink: 0; margin-top: 0.1rem; }
.doc-bq-icon-info { color: var(--primary); opacity: 0.7; }
.doc-bq-icon-warn { color: #eab308; }
.doc-bq-text { flex: 1; min-width: 0; }

/* ═══════ Landing Page ═══════ */
.doc-landing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; max-width: 580px; width: 100%; padding: 0 1rem; }
.doc-landing-card {
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
  padding: 1.25rem 0.75rem 1rem; border-radius: 0.75rem;
  border: 1px solid var(--border); background: var(--card);
  transition: all 0.2s; position: relative; overflow: hidden;
}
.doc-landing-card:hover {
  border-color: color-mix(in oklch, var(--primary) 40%, transparent);
  background: color-mix(in oklch, var(--accent) 50%, transparent);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px color-mix(in oklch, var(--primary) 12%, transparent);
}
.doc-landing-icon { color: var(--muted-foreground); opacity: 0.5; transition: all 0.2s; }
.doc-landing-card:hover .doc-landing-icon { color: var(--primary); opacity: 0.7; transform: scale(1.1); }
.doc-landing-title { font-size: 0.72rem; font-weight: 600; text-align: center; line-height: 1.3; color: var(--foreground); opacity: 0.8; }
.doc-landing-badge { opacity: 0.7; }

/* Start Here card */
.doc-landing-card-start { border-color: color-mix(in oklch, var(--primary) 50%, transparent) !important; box-shadow: 0 0 16px color-mix(in oklch, var(--primary) 15%, transparent); }
.doc-landing-card-start:hover { box-shadow: 0 0 24px color-mix(in oklch, var(--primary) 25%, transparent), 0 4px 12px color-mix(in oklch, var(--primary) 15%, transparent); }
.doc-start-badge { position: absolute; top: 0.35rem; right: 0.35rem; font-size: 0.5rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; background: var(--primary); color: var(--primary-foreground); padding: 0.15rem 0.4rem; border-radius: 0.25rem; line-height: 1; }
.doc-landing-footer { margin-top: 2rem; font-size: 0.68rem; color: var(--muted-foreground); opacity: 0.35; letter-spacing: 0.02em; }

/* ═══════ Selection ═══════ */
.doc-content ::selection { background: color-mix(in oklch, var(--primary) 30%, transparent); }

/* ═══════ Transitions ═══════ */
.doc-content .doc-cb, .doc-content .doc-tw, .doc-content .doc-bq, .doc-content .doc-h2, .doc-content .doc-h3 { transition: opacity 0.2s; }
`
